import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Calendar,
  ChevronLeft,
  RefreshCw,
  Scale,
  Search,
  User,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { resolveBackendUrl } from '@/config/apiConfig';
import { appointmentService, getLocalizedValue } from '@/services/api';
import { parseApiDateTime } from '@/lib/dateTime';

const getLocalizedText = (value, lang) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  return getLocalizedValue(value, lang)?.trim() || '';
};

const getFirstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const normalizeImageUrl = (value) => {
  if (!value || typeof value !== 'string') return '';
  const image = value.trim();
  if (!image) return '';
  if (
    image.startsWith('http://') ||
    image.startsWith('https://') ||
    image.startsWith('data:')
  ) {
    return image;
  }
  if (image.startsWith('/')) {
    return resolveBackendUrl(image);
  }
  return image;
};

const parseDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const readNumber = (...values) => {
  for (const value of values) {
    if (value == null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
};

const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  const today = new Date();
  let years = today.getFullYear() - dateOfBirth.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > dateOfBirth.getMonth() ||
    (today.getMonth() === dateOfBirth.getMonth() &&
      today.getDate() >= dateOfBirth.getDate());
  if (!hasBirthdayPassed) {
    years -= 1;
  }
  return years > 0 ? years : null;
};

const getAppointmentDate = (appointment) =>
  parseApiDateTime(
    appointment?.appointment_at ||
      appointment?.appointment_date ||
      appointment?.start_at ||
      appointment?.date
  );

const hasPatientName = (appointment, lang) => {
  const patient = appointment?.patient || {};
  const firstName = getLocalizedText(patient.first_name, lang);
  const lastName = getLocalizedText(patient.last_name, lang);
  return Boolean(
    getFirstText(
      appointment?.patient_name,
      getLocalizedText(patient.name, lang),
      `${firstName} ${lastName}`.trim()
    )
  );
};

const hasPatientImage = (appointment) => {
  const patient = appointment?.patient || {};
  return Boolean(
    normalizeImageUrl(
      patient.image ||
        patient.avatar ||
        patient.media?.[0]?.url ||
        patient.media?.[0]?.thumb ||
        appointment?.patient_image
    )
  );
};

const hasPatientMetrics = (appointment) => {
  const patient = appointment?.patient || {};
  return Boolean(
    readNumber(patient.age, appointment?.patient_age) ||
      readNumber(patient.weight, appointment?.patient_weight) ||
      parseDate(
        patient.date_of_birth ||
          patient.birthday ||
          appointment?.patient_date_of_birth ||
          appointment?.patient_birthday
      )
  );
};

const getPatientSummaryFromAppointment = (appointment, lang) => {
  const patient = appointment?.patient || {};
  const patientId = Number(patient.id || appointment?.patient_id || 0);
  const firstName = getLocalizedText(patient.first_name, lang);
  const lastName = getLocalizedText(patient.last_name, lang);
  const patientName = getFirstText(
    appointment?.patient_name,
    getLocalizedText(patient.name, lang),
    `${firstName} ${lastName}`.trim()
  );
  const dateOfBirth = parseDate(
    patient.date_of_birth ||
      patient.birthday ||
      appointment?.patient_date_of_birth ||
      appointment?.patient_birthday
  );
  const age = readNumber(patient.age, appointment?.patient_age) || calculateAge(dateOfBirth);
  const weight = readNumber(patient.weight, appointment?.patient_weight);
  const latestAppointment = getAppointmentDate(appointment);
  const serviceName = getFirstText(
    appointment?.service_name,
    getLocalizedText(appointment?.doctor_service?.name, lang)
  );
  const clinicName = getFirstText(
    appointment?.clinic_name,
    getLocalizedText(appointment?.clinic?.name, lang)
  );

  return {
    id: patientId,
    name: patientName || `${lang === 'ar' ? 'مريض' : 'Patient'} #${patientId || ''}`.trim(),
    imageUrl: normalizeImageUrl(
      patient.image ||
        patient.avatar ||
        patient.media?.[0]?.url ||
        patient.media?.[0]?.thumb ||
        appointment?.patient_image
    ),
    appointmentCount: 1,
    doneAppointmentCount: isCompletedAppointment(appointment) ? 1 : 0,
    latestAppointment,
    dateOfBirth,
    age,
    weight,
    serviceLabel: [serviceName, clinicName].filter(Boolean).join(' - '),
  };
};

const isCompletedAppointment = (appointment) => {
  const state = getFirstText(
    appointment?.state,
    appointment?.status,
    appointment?.appointment_status?.name
  ).toLowerCase();

  if (state) {
    return state === 'done' || state === 'completed';
  }

  return Number(appointment?.appointment_status_id) === 4;
};

const buildPatientSummaries = (appointments, lang) => {
  const grouped = new Map();

  appointments.forEach((appointment) => {
    const patientId = Number(appointment?.patient?.id || appointment?.patient_id || 0);
    if (!patientId) return;
    if (!grouped.has(patientId)) {
      grouped.set(patientId, []);
    }
    grouped.get(patientId).push(appointment);
  });

  return Array.from(grouped.entries())
    .map(([patientId, rows]) => {
      const sortedRows = [...rows].sort((a, b) => {
        const first = getAppointmentDate(a)?.getTime() || 0;
        const second = getAppointmentDate(b)?.getTime() || 0;
        return second - first;
      });

      const latest = sortedRows[0];
      const namedRow = sortedRows.find((item) => hasPatientName(item, lang)) || latest;
      const imageRow = sortedRows.find(hasPatientImage) || latest;
      const metricsRow = sortedRows.find(hasPatientMetrics) || latest;

      const latestSummary = getPatientSummaryFromAppointment(latest, lang);
      const namedSummary = getPatientSummaryFromAppointment(namedRow, lang);
      const imageSummary = getPatientSummaryFromAppointment(imageRow, lang);
      const metricsSummary = getPatientSummaryFromAppointment(metricsRow, lang);

      return {
        id: patientId,
        name: namedSummary.name,
        imageUrl: imageSummary.imageUrl,
        appointmentCount: sortedRows.length,
        doneAppointmentCount: sortedRows.filter(isCompletedAppointment).length,
        latestAppointment: latestSummary.latestAppointment,
        dateOfBirth: metricsSummary.dateOfBirth,
        age: metricsSummary.age,
        weight: metricsSummary.weight,
        serviceLabel: latestSummary.serviceLabel,
      };
    })
    .sort((a, b) => {
      const first = a.latestAppointment?.getTime() || 0;
      const second = b.latestAppointment?.getTime() || 0;
      return second - first;
    });
};

const formatWeight = (weight) =>
  weight != null && weight > 0 ? `${Math.round(weight)} KG` : '--';

const formatAge = (age, dateOfBirth) => {
  const resolvedAge = age || calculateAge(dateOfBirth);
  return resolvedAge != null && resolvedAge > 0 ? `${resolvedAge}` : '--';
};

const DoctorPatients = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;
  const isArabic = lang === 'ar';

  const [patients, setPatients] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appointmentService.getAllAppointments();
      const appointments = response.data?.data || response.data || [];
      const summaries = buildPatientSummaries(appointments, lang);
      setPatients(summaries);
      setFilteredPatients(summaries);
    } catch (error) {
      console.error('Failed to fetch doctor patients:', error);
      toast.error(
        isArabic ? 'تعذر تحميل قائمة المرضى' : 'Failed to load patients'
      );
      setPatients([]);
      setFilteredPatients([]);
    } finally {
      setLoading(false);
    }
  }, [isArabic, lang]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchPatients();
  }, [fetchPatients, isAuthenticated, navigate]);

  useEffect(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      setFilteredPatients(patients);
      return;
    }

    setFilteredPatients(
      patients.filter((patient) => patient.name.toLowerCase().includes(query))
    );
  }, [patients, searchTerm]);

  if (!isAuthenticated) return null;

  return (
    <div
      className="min-h-screen bg-slate-50 dark:bg-slate-900"
      data-testid="doctor-patients"
    >
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/doctor/dashboard')}
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className={isArabic ? 'text-right' : 'text-left'}>
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {isArabic ? 'المرضى' : 'Patients'}
                </h1>
                <p className="text-slate-500 dark:text-slate-400">
                  {isArabic
                    ? `هؤلاء المرضى لديهم مواعيد محجوزة معك`
                    : 'These are patients booked appointments with your doctors'}
                </p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
                  {isArabic
                    ? `${filteredPatients.length} مريض`
                    : `${filteredPatients.length} patients`}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchPatients}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        <Card className="dark:bg-slate-800 border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={isArabic ? 'ابحث باسم المريض' : 'Search by patient name'}
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="pl-10"
                data-testid="search-patients"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="dark:bg-slate-800 border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {isArabic ? 'قائمة المرضى' : 'Patient List'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className="rounded-3xl border dark:border-slate-700 p-5"
                  >
                    <div className="flex items-start gap-4">
                      <Skeleton className="h-28 w-28 rounded-3xl shrink-0" />
                      <div className="flex-1 space-y-3">
                        <Skeleton className="h-6 w-1/3 ml-auto" />
                        <Skeleton className="h-px w-full" />
                        <div className="flex gap-3 justify-end">
                          <Skeleton className="h-5 w-14" />
                          <Skeleton className="h-5 w-16" />
                          <Skeleton className="h-5 w-12" />
                        </div>
                        <Skeleton className="h-4 w-1/2 ml-auto" />
                        <Skeleton className="h-4 w-1/3 ml-auto" />
                        <Skeleton className="h-4 w-2/5 ml-auto" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredPatients.length === 0 ? (
              <div className="text-center py-12">
                <User className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
                <p className="text-slate-600 dark:text-slate-300 font-medium">
                  {searchTerm
                    ? isArabic
                      ? 'لا يوجد مرضى مطابقون'
                      : 'No matching patients'
                    : isArabic
                      ? 'لا يوجد مرضى بعد'
                      : 'No patients yet'}
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                  {searchTerm
                    ? isArabic
                      ? 'جرّب البحث باسم آخر للمريض.'
                      : 'Try searching with another patient name.'
                    : isArabic
                      ? 'سيظهر المرضى هنا بعد إنشاء الحجوزات.'
                      : 'Patients appear here after bookings are created.'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredPatients.map((patient) => (
                  <button
                    key={patient.id}
                    type="button"
                    className="w-full rounded-3xl border dark:border-slate-700 p-5 text-inherit transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/40"
                    onClick={() => navigate(`/doctor/patients/${patient.id}`)}
                    data-testid={`patient-${patient.id}`}
                  >
                    <div
                      className={`flex flex-col gap-4 sm:items-start ${
                        isArabic ? 'sm:flex-row-reverse text-right' : 'sm:flex-row text-left'
                      }`}
                    >
                      <Avatar className="h-28 w-28 rounded-3xl shrink-0 self-center sm:self-auto">
                        <AvatarImage
                          src={patient.imageUrl}
                          alt={patient.name}
                          className="object-cover"
                        />
                        <AvatarFallback className="rounded-3xl bg-teal-100 text-teal-700 text-3xl font-bold dark:bg-teal-900/50 dark:text-teal-200">
                          {patient.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                          {patient.name}
                        </h3>

                        <div className="my-3 h-px bg-slate-200 dark:bg-slate-700" />

                        <div
                          className={`flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600 dark:text-slate-300 ${
                            isArabic ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            <span>{formatAge(patient.age, patient.dateOfBirth)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Scale className="h-4 w-4" />
                            <span>{formatWeight(patient.weight)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{patient.appointmentCount}</span>
                          </div>
                        </div>

                        <p className="mt-4 text-base font-semibold text-slate-900 dark:text-white">
                          {isArabic
                            ? `إجمالي المواعيد: ${patient.appointmentCount}`
                            : `Total appointments: ${patient.appointmentCount}`}
                        </p>
                        <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-400">
                          {isArabic
                            ? `المواعيد المكتملة: ${patient.doneAppointmentCount}`
                            : `Completed appointments: ${patient.doneAppointmentCount}`}
                        </p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          {isArabic ? 'آخر موعد' : 'Latest'}:{' '}
                          {patient.latestAppointment
                            ? format(patient.latestAppointment, 'dd MMM yyyy', { locale })
                            : '--'}
                        </p>
                        {patient.serviceLabel ? (
                          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            {patient.serviceLabel}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DoctorPatients;
